import { Injectable, signal } from '@angular/core';
import { catchError, EMPTY, finalize, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import {
  InventoryTransaction,
  InventoryTransactionLine,
  InventoryTransactionStatus,
  InventoryTransactionType,
} from '../../models/inventory/inventory-transaction';
import _ from 'lodash';
import { ConfirmationService } from 'primeng/api';
import { InventoryTransactionProxyService } from '../../proxies/inventory/inventory-transaction.proxy.service';
import { EntityKeyByNumber } from '../../models/inventory/keys/entity-key';
import { Roll } from '../../models/production/roll';
import { RollProxyService } from '../../proxies/production/roll.proxy';
import { InventoryTransactionManagerKey } from '../factory/inventory-transaction.factory';
import { TranslateService } from '@ngx-translate/core';
import { Translation } from '../../models/translation';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { LocationNode } from '../../models/location';
import { LocationProxyService } from '../../proxies/inventory/location.proxy.service';
import { EntityKeyByReference } from '@card-software/library';

@Injectable()
export class InventoryTransactionService {
  readonly inventoryTransactions = signal<InventoryTransaction[]>([]);
  readonly entityKey = signal<EntityKeyByReference | null>(null);
  readonly comingFromLocationCode = signal<string | null>(null);
  readonly goingToLocationCode = signal<string | null>(null);
  private readonly locations = signal<LocationNode[]>([]);
  public type: InventoryTransactionManagerKey = 'warehouse-import';

  public rollsRecordInventoryTransaction: Record<string, Roll[]> = {};

  public lotItemLoadStatus: Record<string, boolean> = {};
  private translations: Record<string, Translation> = {};
  private readonly operationLocks = new Set<string>();
  private readonly isProcessingAny = signal<boolean>(false);
  private isCreatingTransaction = false;
  private pendingTransactionCreation$: Observable<InventoryTransaction | null | undefined> | null = null;

  constructor(
    private readonly inventoryTransactionProxyService: InventoryTransactionProxyService,
    private readonly confirmationService: ConfirmationService,
    private readonly rollProxyService: RollProxyService,
    private readonly translateService: TranslateService,
    private readonly locationProxyService: LocationProxyService,
  ) {
    this.initializeTranslations();
    this.translateService.onLangChange.subscribe(() => {
      this.initializeTranslations();
    });

    this.locationProxyService.getAll().subscribe((result) => {
      if (result.success && result.data) {
        this.locations.set(result.data);
      }
    });
  }

  private initializeTranslations(): void {
    this.translations = {
      Lot_Item_Action: {
        es: `Item con número: {{number}} fue {{action}} correctamente.`,
        en: `Lot item with number: {{number}} was {{action}} successfully.`,
      },
      Added: {
        es: 'agregado',
        en: 'added',
      },
      Deleted: {
        es: 'eliminado',
        en: 'deleted',
      },
      Lot_Item_Already_In_List_Title: {
        es: 'Ya está en la lista',
        en: 'Already in list',
      },
      Lot_Item_Already_In_List_Message: {
        es: 'El item {{number}} ya fue agregado a la transacción.',
        en: 'Lot item {{number}} has already been added to the transaction.',
      },
    };

    // Registrar las traducciones con el servicio de traducción
    Object.entries(this.translations).forEach(([key, translation]) => {
      this.translateService.setTranslation('es', { [key]: translation.es }, true);
      this.translateService.setTranslation('en', { [key]: translation.en }, true);
    });
  }

  //#region Refreshers
  public reloadInventoryTransactions(inventoryTxType?: InventoryTransactionType): void {
    if (!this.entityKey()) {
      console.warn('Entity key is not set. Cannot reload inventory transactions.');
      return;
    }
    this.inventoryTransactionProxyService
      .getRelatedInventoryTransactionsByEntityKey(this.entityKey()!, true)
      .pipe(
        catchError(() => {
          if (!_.isNil(inventoryTxType)) {
            this.createLocalInventoryTransaction(inventoryTxType);
          }
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        if (response.success && response.data && response.data.length > 0) {
          this.inventoryTransactions.set(response.data);

          if (this.type === 'transfer-unit-details') {
            response.data.forEach((transaction) => {
              this.getLotItemKeysRolls(transaction);
            });
          }
        } else {
          if (!_.isNil(inventoryTxType)) {
            this.createLocalInventoryTransaction(inventoryTxType);
          }
        }
      });
  }

  //#endregion

  //#region Operation Locks
  public isOperationInProgress(): boolean {
    return this.isProcessingAny();
  }

  private lockOperation(operationKey: string): boolean {
    if (this.operationLocks.has(operationKey)) {
      return false; // Operation already locked
    }
    this.operationLocks.add(operationKey);
    this.updateProcessingState();
    return true;
  }

  private unlockOperation(operationKey: string): void {
    this.operationLocks.delete(operationKey);
    this.updateProcessingState();
  }

  private updateProcessingState(): void {
    this.isProcessingAny.set(this.operationLocks.size > 0 || this.isCreatingTransaction);
  }
  //#endregion

  //#region Getters
  public getRollsByInventoryTransaction(inventoryTransactionReferences: string): Roll[] {
    const rolls = this.rollsRecordInventoryTransaction[inventoryTransactionReferences] || [];
    ///AQUIIIIIIII
    rolls.filter((roll) => {
      const invTransaction = this.inventoryTransactions().find((transaction) => transaction.id === inventoryTransactionReferences);
      if (invTransaction) {
        const lotItemKeys = invTransaction.lines.filter((line) =>
          _.isNil(line.lotItemKey) ? false : line.lotItemKey.number === roll.number,
        );
        if (lotItemKeys.length > 0) {
          roll.number = lotItemKeys[0].lotItemKey!.number;
          roll.netQuantity = lotItemKeys[0].quantity;
        }
      }
    });

    return rolls;
  }
  public getLotItemKeysRolls(inventoryTransaction: InventoryTransaction): void {
    const lotItemKeyLines = inventoryTransaction.lines.filter((line) => !_.isNil(line.lotItemKey));
    const numbers = lotItemKeyLines.map((key) => key.lotItemKey!.number);
    if (_.isEmpty(numbers)) return;

    this.rollProxyService
      .getByRollNumbers(numbers)
      .pipe(
        catchError(() => {
          return EMPTY;
        }),
        tap((response) => {
          if (response.success && response.data) {
            this.rollsRecordInventoryTransaction[inventoryTransaction.id!] = response.data;
          }
        }),
      )
      .subscribe();
  }
  public isLotItemKeyLoading(lotItemKey: EntityKeyByNumber): boolean {
    return this.lotItemLoadStatus[this.getLotItemKeyString(lotItemKey)] || false;
  }

  public getOpenInventoryTransaction(): InventoryTransaction | null {
    return this.inventoryTransactions().find((transaction) => transaction.status === InventoryTransactionStatus.Draft) || null;
  }

  public getLocation(locationCode: string): LocationNode | null {
    const location = this.locations().find((loc) => loc.locationCode === locationCode);
    return location || null;
  }
  //#endregion

  //#region Setters
  public setType(type: InventoryTransactionManagerKey) {
    this.type = type;
  }
  public setInitiatorEntityKey(entityKey: EntityKeyByReference, inventoryTxType?: InventoryTransactionType) {
    this.entityKey.set(entityKey);
    this.inventoryTransactions.set([]);
    this.reloadInventoryTransactions(inventoryTxType);
  }

  public setComingFromCode(transferKey: string) {
    this.comingFromLocationCode.set(transferKey);
  }
  public setGoingToCode(transferKey: string) {
    this.goingToLocationCode.set(transferKey);
  }

  //#endregion

  //#region Rolls Setters
  public addRollToRecord(inventoryTransactionReference: string, roll: Roll) {
    if (!this.rollsRecordInventoryTransaction[inventoryTransactionReference]) {
      this.rollsRecordInventoryTransaction[inventoryTransactionReference] = [];
    }
    const existingRollIndex = this.rollsRecordInventoryTransaction[inventoryTransactionReference].findIndex(
      (r) => r.number === roll.number,
    );

    if (existingRollIndex === -1) {
      this.rollsRecordInventoryTransaction[inventoryTransactionReference].push(roll);
      GlobalMessageUtil.success('Roll added', `Roll with number ${roll.number} added to the transfer unit.`);
    } else {
      GlobalMessageUtil.warn('Roll already exists', `Roll with number ${roll.number} already exists in the transfer unit.`);
    }
  }

  public removeRollFromRecord(inventoryTransactionReference: string, roll: Roll) {
    if (this.rollsRecordInventoryTransaction[inventoryTransactionReference]) {
      const index = this.rollsRecordInventoryTransaction[inventoryTransactionReference].findIndex((r) => r.number === roll.number);
      if (index !== -1) {
        this.rollsRecordInventoryTransaction[inventoryTransactionReference].splice(index, 1);
        GlobalMessageUtil.success('Roll removed', `Roll with number ${roll.number} removed from the transfer unit.`);
      } else {
        GlobalMessageUtil.warn('Roll not found', `Roll with number ${roll.number} not found in the transfer unit.`);
      }
    } else {
      GlobalMessageUtil.warn('No rolls found', `No rolls found for transfer unit ${inventoryTransactionReference}.`);
    }
  }

  //#region Local CRUD

  public createLocalInventoryTransaction(inventoryTxType: InventoryTransactionType) {
    const existingDraft = this.inventoryTransactions().find((transaction) => _.isNil(transaction.id));

    if (!this.comingFromLocationCode() || !this.goingToLocationCode() || !this.entityKey()) {
      console.warn('Missing required keys to create inventory transaction.');
      return;
    } else if (existingDraft) {
      console.warn('Draft inventory transaction already exists. Please update or delete it before creating a new one.');
      return;
    } else if (_.isNil(this.entityKey())) {
      GlobalMessageUtil.warn('Entity key not set', 'Please set the entity key before creating an inventory transaction.');
      return;
    }

    const inventoryTransaction: InventoryTransaction = {
      status: InventoryTransactionStatus.Draft,
      initiator: this.entityKey()!,
      transactionType: inventoryTxType,
      fromLocationCode: this.comingFromLocationCode()!,
      toLocationCode: this.goingToLocationCode()!,
      notes: [],
      lines: [],
    };

    this.inventoryTransactions.update((transactions) => [...transactions, inventoryTransaction]);
  }

  updateLocalInventoryTransaction(inventoryTransaction: InventoryTransaction) {
    const index = this.inventoryTransactions().findIndex((transaction) => transaction.id === inventoryTransaction.id);
    if (index !== -1) {
      this.inventoryTransactions.update((transactions) => {
        const updatedTransactions = [...transactions];
        updatedTransactions[index] = inventoryTransaction;
        return updatedTransactions;
      });
    } else {
      GlobalMessageUtil.warn(
        'Inventory transaction not found',
        'The inventory transaction you are trying to update does not exist. Please contact support.',
      );
    }
  }

  deleteLocalInventoryTransaction(inventoryTransaction: InventoryTransaction) {
    const index = this.inventoryTransactions().findIndex((transaction) => transaction.id === inventoryTransaction.id);
    if (index !== -1) {
      this.inventoryTransactions.update((transactions) => {
        const updatedTransactions = [...transactions];
        updatedTransactions.splice(index, 1);
        return updatedTransactions;
      });
    } else {
      GlobalMessageUtil.warn(
        'Inventory transaction not found',
        'The inventory transaction you are trying to delete does not exist. Please contact support.',
      );
    }
  }

  //#endregion
  private getOrStartTransactionCreation(): Observable<InventoryTransaction | null | undefined> {
    if (!this.pendingTransactionCreation$) {
      this.pendingTransactionCreation$ = this.createInventoryTransaction().pipe(
        finalize(() => {
          this.pendingTransactionCreation$ = null;
        }),
        shareReplay(1),
      );
    }
    return this.pendingTransactionCreation$;
  }

  public upsertLotItemKey(
    lotItemKey: EntityKeyByNumber,
    add: boolean,
    inventoryReference?: string,
    displayResultMessages = true,
  ): Observable<boolean | null | undefined> {
    const operationKey = `upsert_${this.getLotItemKeyString(lotItemKey)}_${add ? 'add' : 'remove'}`;

    // Check if this specific operation is already in progress
    if (!this.lockOperation(operationKey)) {
      console.warn(`Operation ${operationKey} is already in progress, skipping...`);
      return EMPTY;
    }

    const inventoryTransaction = this.inventoryTransactions().find((t) => t.id === inventoryReference);

    if (!inventoryTransaction) {
      this.unlockOperation(operationKey);
      GlobalMessageUtil.warn('Inventory transaction not found', 'Please select an inventory transaction before adding lot item keys.');
      return EMPTY;
    }

    this.lotItemLoadStatus[this.getLotItemKeyString(lotItemKey)] = true;

    const transactionId$ = _.isNil(inventoryTransaction.id)
      ? this.getOrStartTransactionCreation().pipe(
          map((response) => response?.id),
          catchError(() => {
            this.lotItemLoadStatus[this.getLotItemKeyString(lotItemKey)] = false;
            this.unlockOperation(operationKey);
            return of(undefined);
          }),
        )
      : of(inventoryTransaction.id);

    return transactionId$.pipe(
      switchMap((transactionId) => {
        if (!transactionId) {
          this.unlockOperation(operationKey);
          return EMPTY;
        }

        return this.inventoryTransactionProxyService.upsertLotItemKey(lotItemKey, add, transactionId).pipe(
          catchError(() => {
            this.lotItemLoadStatus[this.getLotItemKeyString(lotItemKey)] = false;
            this.unlockOperation(operationKey);
            return EMPTY;
          }),
          tap((response) => {
            this.lotItemLoadStatus[this.getLotItemKeyString(lotItemKey)] = false;
            this.unlockOperation(operationKey);

            if (response.success && response.data) {
              if (displayResultMessages) {
                const action = this.translateService.instant(add ? 'Added' : 'Deleted');
                GlobalMessageUtil.success(
                  'Success',
                  this.translateService.instant('Lot_Item_Action', {
                    number: `${lotItemKey.number}`,
                    action: action,
                  }),
                );
              }

              this.inventoryTransactions.update((transactions) => {
                const updated = [...transactions];
                const index = updated.findIndex((t) => t.id === transactionId);
                if (index !== -1) {
                  updated[index].lines = response.data!.lines;
                }
                return updated;
              });
            }
          }),
          map(() => true),
        );
      }),
    );
  }

  //#region LotItem Remote CRUD

  //#endregion

  public static GetLotItemKeyLines(inventoryTransaction: InventoryTransaction): InventoryTransactionLine[] {
    return inventoryTransaction.lines.filter((line) => !_.isNil(line.lotItemKey));
  }

  public static GetLotItemKeys(inventoryTransaction: InventoryTransaction): EntityKeyByNumber[] {
    return inventoryTransaction.lines.filter((line) => !_.isNil(line.lotItemKey)).map((line) => line.lotItemKey!);
  }

  //#region Remote CRUD
  public createInventoryTransaction(): Observable<InventoryTransaction | null | undefined> {
    const inventoryTransaction = this.inventoryTransactions().find((transaction) => _.isNil(transaction.id));

    if (!inventoryTransaction) {
      GlobalMessageUtil.warn('No draft inventory transaction found', 'Please create a draft inventory transaction before submitting.');
      return EMPTY;
    }

    // Set creation state and update processing
    this.isCreatingTransaction = true;
    this.updateProcessingState();

    return this.inventoryTransactionProxyService.insert(inventoryTransaction).pipe(
      catchError(() => {
        this.isCreatingTransaction = false;
        this.updateProcessingState();
        return EMPTY;
      }),
      tap(() => {
        // Release the creation lock
        this.isCreatingTransaction = false;
        this.updateProcessingState();
      }),
      map((response) => {
        if (response.success) {
          GlobalMessageUtil.success('Success', 'Inventory transaction created successfully.');

          this.inventoryTransactions.update((transactions) => {
            const updatedTransactions = [...transactions];
            const index = updatedTransactions.findIndex((transaction) => _.isNil(transaction.id));
            if (index !== -1) {
              if (response.data) {
                updatedTransactions[index] = response.data;
              }
            }
            return updatedTransactions;
          });
          return response.data;
        } else {
          return;
        }
      }),
    );
  }

  public moveInventoryTransactionOutOfDraft(inventoryTransactionReference: string, entityLotItemsCompleted: boolean) {
    const inventoryTransaction = this.inventoryTransactions().find((transaction) => transaction.id === inventoryTransactionReference);

    if (!inventoryTransaction) {
      GlobalMessageUtil.warn('No draft inventory transaction found', 'Please create a draft inventory transaction before submitting.');
      return;
    } else if (inventoryTransaction.status !== InventoryTransactionStatus.Draft) {
      GlobalMessageUtil.warn('Inventory transaction already submitted', 'The inventory transaction has already been submitted.');
      return;
    }

    if (!entityLotItemsCompleted) {
      this.confirmationService.confirm({
        key: 'main',
        message: 'Are you sure you want to move this inventory transaction out of draft? There are items with missing quantities.',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.inventoryTransactionProxyService
            .moveOutOfDraft(inventoryTransactionReference)
            .pipe(
              catchError(() => {
                return EMPTY;
              }),
            )
            .subscribe((response) => {
              if (response.success) {
                GlobalMessageUtil.success('Success', 'Inventory transaction moved out of draft successfully.');
                this.inventoryTransactions.update((transactions) => {
                  const updatedTransactions = [...transactions];
                  const index = updatedTransactions.findIndex((transaction) => transaction.id === inventoryTransactionReference);
                  if (index !== -1) {
                    if (response.data) {
                      updatedTransactions[index] = response.data;
                    }
                  }
                  return updatedTransactions;
                });
              }
            });
        },
        reject: () => {
          return;
        },
      });
    } else {
      this.confirmationService.confirm({
        key: 'main',
        message: 'Are you sure you want to move this inventory transaction out of draft? This action cannot be undone.',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.inventoryTransactionProxyService
            .moveOutOfDraft(inventoryTransactionReference)
            .pipe(
              catchError(() => {
                return EMPTY;
              }),
            )
            .subscribe((response) => {
              if (response.success) {
                GlobalMessageUtil.success('Success', 'Inventory transaction moved out of draft successfully.');
                this.inventoryTransactions.update((transactions) => {
                  const updatedTransactions = [...transactions];
                  const index = updatedTransactions.findIndex((transaction) => transaction.id === inventoryTransactionReference);
                  if (index !== -1) {
                    if (response.data) {
                      updatedTransactions[index] = response.data;
                    }
                  }
                  return updatedTransactions;
                });
              }
            });
        },
        reject: () => {
          return;
        },
      });
    }
  }

  public saveInventoryTransaction(inventoryTransactionReference: string) {
    const inventoryTransaction = this.inventoryTransactions().find((transaction) => transaction.id === inventoryTransactionReference);
    if (!inventoryTransaction) {
      GlobalMessageUtil.warn('No draft inventory transaction found', 'Please create a draft inventory transaction before submitting.');
      return;
    } else if (inventoryTransaction.status !== InventoryTransactionStatus.Draft) {
      GlobalMessageUtil.warn('Inventory transaction already submitted', 'The inventory transaction has already been submitted.');
      return;
    }
    this.inventoryTransactionProxyService
      .update(inventoryTransaction)
      .pipe(
        catchError(() => {
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        if (response.success) {
          GlobalMessageUtil.success('Success', 'Inventory transaction updated successfully.');
          this.inventoryTransactions.update((transactions) => {
            const updatedTransactions = [...transactions];
            const index = updatedTransactions.findIndex((transaction) => transaction.id === inventoryTransactionReference);
            if (index !== -1) {
              if (response.data) {
                updatedTransactions[index] = response.data;
              }
            }
            return updatedTransactions;
          });
          return response.data;
        } else {
          return;
        }
      });
  }
  //#endregion

  //#endregion
  public getLotItemKeyString(lotItemKey: EntityKeyByNumber): string {
    return `${lotItemKey.entityDefinitionCode}-${lotItemKey.number}`;
  }
}
